/**
 * WeaveIn.ai Platform - Predictions API Service
 * Real-time ML prediction endpoints for churn risk and model performance
 */

import axios from 'axios';
import { getApiBaseUrl } from '@/lib/utils';

const API_BASE = getApiBaseUrl();

export interface PredictionResult {
  member_id: number;
  enrollment_id: number | null;
  prediction_type: string;
  horizon_days: number;
  churn_score: number;
  risk_level: string;
  top_factors: Array<{
    feature: string;
    importance: number;
    value: any;
  }>;
  confidence: number;
  model_version: string | null;
  from_cache: boolean;
  computed_at: string;
}

export interface PredictionHistoryEntry {
  churn_score: number;
  risk_level: string;
  confidence: number | null;
  computed_at: string | null;
  model_version: string | null;
}

export interface ModelPerformance {
  unused_outcomes_for_training: number;
  outcome_distribution: Record<string, number>;
  recent_accuracy_30d: number | null;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (cboId) headers['X-CBO-ID'] = cboId;
  return headers;
};

export const predictionsAPI = {
  /**
   * Get real-time churn prediction for a member
   */
  getMemberPrediction: async (
    memberId: number,
    horizonDays: number = 30,
    predictionType: string = 'churn',
    forceRefresh: boolean = false,
  ): Promise<PredictionResult> => {
    const response = await axios.get(
      `${API_BASE}/api/retention/members/${memberId}/predict`,
      {
        params: {
          horizon_days: horizonDays,
          prediction_type: predictionType,
          force_refresh: forceRefresh,
        },
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  /**
   * Get prediction history for trend charts
   */
  getMemberPredictionHistory: async (
    memberId: number,
    horizonDays: number = 30,
    limit: number = 30,
  ): Promise<{ member_id: number; horizon_days: number; history: PredictionHistoryEntry[] }> => {
    const response = await axios.get(
      `${API_BASE}/api/retention/members/${memberId}/prediction-history`,
      {
        params: { horizon_days: horizonDays, limit },
        headers: getAuthHeaders(),
      },
    );
    return response.data;
  },

  /**
   * Get ML model performance metrics
   */
  getModelPerformance: async (): Promise<ModelPerformance> => {
    const response = await axios.get(
      `${API_BASE}/api/retention/model-performance`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
};
